import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import {
    Folder, FolderOpen, ChevronRight, ChevronDown, Search, FileText, Loader2,
    Building2, Calendar, AlertTriangle, Briefcase, GraduationCap, Inbox,
} from "lucide-react";

const FILIERE_LABEL = {
    electrotechnique: "BTS Électrotechnique",
    ciel: "BTS CIEL",
};
const NIVEAU_LABEL = { "1ere": "1ère année", "2eme": "2ème année" };

const STORAGE_KEY = "profawz-bilans-tree-open";

function loadOpen() {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
}
function saveOpen(state) {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* ignore */ }
}

/**
 * BilansTreeView — file-explorer style display of bilans, organised as
 *   Année scolaire → BTS → Niveau (1ère / 2ème) → Bilans
 * Replaces the flat grid on the dashboard so responsables/formateurs find
 * historical bilans easily and current ones aren't drowned by old ones.
 */
export default function BilansTreeView({ isFormateur, onNiveauChange }) {
    const [tree, setTree] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(loadOpen());
    const [search, setSearch] = useState("");
    const [filterPeriod, setFilterPeriod] = useState("all");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/bilans/tree");
                setTree(Array.isArray(data) ? data : []);
                // Auto-open the most recent year on first load (when no state)
                if (Array.isArray(data) && data.length && Object.keys(loadOpen()).length === 0) {
                    setOpen({ [`y:${data[0].year}`]: true });
                }
            } catch (e) { /* ignore */ }
            finally { setLoading(false); }
        })();
    }, [onNiveauChange]); // re-fetch when caller signals a niveau change

    useEffect(() => { saveOpen(open); }, [open]);

    const toggle = (key) => setOpen((s) => ({ ...s, [key]: !s[key] }));

    // List of all distinct periods across the tree (for the filter dropdown).
    // Sorted descending so most recent first.
    const periodOptions = useMemo(() => {
        const seen = new Map();
        tree.forEach((y) => y.filieres.forEach((f) => f.niveaux.forEach((n) =>
            (n.periodes || []).forEach((p) => {
                if (!seen.has(p.period)) seen.set(p.period, p.label);
            })
        )));
        return Array.from(seen.entries())
            .sort((a, b) => b[0].localeCompare(a[0]))
            .map(([value, label]) => ({ value, label }));
    }, [tree]);

    // Search filter — when search is non-empty, expand all matching folders and
    // surface only bilans whose nom/prenom/entreprise/email matches. Period
    // filter narrows down to a single date.
    const filteredTree = useMemo(() => {
        const hasSearch = !!search.trim();
        const hasPeriod = filterPeriod !== "all";
        if (!hasSearch && !hasPeriod) return tree;
        const q = search.toLowerCase();
        const matches = (b) => {
            if (!hasSearch) return true;
            const hay = `${b.prenom || ""} ${b.nom || ""} ${b.entreprise || ""} ${b.tuteur || ""} ${b.author_email || ""}`.toLowerCase();
            return hay.includes(q);
        };
        return tree.map((y) => {
            const filieres = y.filieres.map((f) => {
                const niveaux = f.niveaux.map((n) => {
                    const periodes = (n.periodes || [])
                        .filter((p) => !hasPeriod || p.period === filterPeriod)
                        .map((p) => ({
                            ...p,
                            bilans: (p.bilans || []).filter(matches),
                            total: (p.bilans || []).filter(matches).length,
                        }))
                        .filter((p) => p.bilans.length > 0);
                    return { ...n, periodes, total: periodes.reduce((s, p) => s + p.bilans.length, 0) };
                }).filter((n) => n.total > 0);
                return { ...f, niveaux, total: niveaux.reduce((s, n) => s + n.total, 0) };
            }).filter((f) => f.total > 0);
            return { ...y, filieres, total: filieres.reduce((s, f) => s + f.total, 0) };
        }).filter((y) => y.total > 0);
    }, [tree, search, filterPeriod]);

    // Auto-expand all folders when searching or filtering by period
    const isOpen = (key) => (search.trim() || filterPeriod !== "all" ? true : !!open[key]);

    if (loading) {
        return (
            <div className="panel p-10 text-center text-muted2 text-sm" data-testid="bilans-tree-loading">
                <Loader2 size={20} className="animate-spin inline-block mr-2 text-orange" />
                Chargement de l&apos;arborescence…
            </div>
        );
    }

    if (tree.length === 0) {
        return (
            <div className="panel p-12 text-center" data-testid="bilans-tree-empty">
                <Inbox size={36} strokeWidth={1.5} className="text-muted2 mx-auto" />
                <h3 className="h-display text-xl font-semibold mt-4">Aucun bilan archivé</h3>
                <p className="text-sm text-muted2 mt-2 max-w-md mx-auto">
                    Les bilans s&apos;organiseront automatiquement par année scolaire et BTS dès qu&apos;ils seront créés.
                </p>
            </div>
        );
    }

    const totalAll = filteredTree.reduce((s, y) => s + y.total, 0);

    return (
        <div className="space-y-3" data-testid="bilans-tree-view">
            {/* SEARCH + PERIOD FILTER */}
            <div className="panel p-3 flex flex-wrap items-center gap-3 bg-surface">
                <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                    <Search size={14} className="text-muted2 shrink-0" />
                    <input
                        type="search"
                        placeholder="Rechercher un bilan (nom, prénom, entreprise, tuteur…)"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="field-input !py-1.5 !text-xs flex-1"
                        data-testid="bilans-tree-search"
                    />
                </div>
                <div className="flex items-center gap-1.5">
                    <label className="text-[11px] font-mono text-muted2">Période</label>
                    <select
                        className="field-input !py-1 !text-xs"
                        value={filterPeriod}
                        onChange={(e) => setFilterPeriod(e.target.value)}
                        data-testid="bilans-tree-period-filter"
                    >
                        <option value="all">Toutes</option>
                        {periodOptions.map((p) => (
                            <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                    </select>
                </div>
                {(search.trim() || filterPeriod !== "all") && (
                    <button
                        onClick={() => { setSearch(""); setFilterPeriod("all"); }}
                        className="text-[11px] text-orange hover:underline"
                        data-testid="bilans-tree-filter-reset"
                    >
                        Réinitialiser
                    </button>
                )}
                <span className="text-[11px] font-mono text-muted2 shrink-0" data-testid="bilans-tree-count">
                    {totalAll} bilan{totalAll > 1 ? "s" : ""}
                </span>
            </div>

            {filteredTree.length === 0 && (
                <div className="panel p-6 text-center text-sm text-muted2" data-testid="bilans-tree-no-match">
                    Aucun bilan ne correspond à « {search} ».
                </div>
            )}

            {/* TREE */}
            {filteredTree.map((y) => {
                const yKey = `y:${y.year}`;
                const yOpen = isOpen(yKey);
                return (
                    <div key={y.year} className="panel overflow-hidden" data-testid={`tree-year-${y.year}`}>
                        <button
                            type="button"
                            onClick={() => toggle(yKey)}
                            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-orange/5 transition-colors border-b border-rule"
                            data-testid={`tree-year-toggle-${y.year}`}
                        >
                            {yOpen ? <ChevronDown size={16} className="text-orange" /> : <ChevronRight size={16} className="text-muted2" />}
                            {yOpen ? <FolderOpen size={18} className="text-orange" /> : <Folder size={18} className="text-orange/70" />}
                            <span className="h-display text-lg font-semibold">Année scolaire {y.year}</span>
                            <span className="pill bg-orange/10 text-orange text-[10px] !px-2 !py-0.5">{y.total}</span>
                        </button>
                        {yOpen && (
                            <div className="bg-paper">
                                {y.filieres.map((f) => {
                                    const fKey = `f:${y.year}:${f.filiere}`;
                                    const fOpen = isOpen(fKey);
                                    return (
                                        <div key={f.filiere} className="border-b border-rule last:border-b-0">
                                            <button
                                                type="button"
                                                onClick={() => toggle(fKey)}
                                                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-emerald-50/40 transition-colors pl-10"
                                                data-testid={`tree-filiere-toggle-${y.year}-${f.filiere}`}
                                            >
                                                {fOpen ? <ChevronDown size={14} className="text-emerald-700" /> : <ChevronRight size={14} className="text-muted2" />}
                                                {fOpen ? <FolderOpen size={16} className="text-emerald-700" /> : <Folder size={16} className="text-emerald-700/60" />}
                                                <span className="font-medium text-sm">{f.label || FILIERE_LABEL[f.filiere] || f.filiere}</span>
                                                <span className="pill bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] !px-2 !py-0.5">{f.total}</span>
                                            </button>
                                            {fOpen && (
                                                <div className="pl-10">
                                                    {f.niveaux.map((n) => {
                                                        if (!n.total) return null;
                                                        const nKey = `n:${y.year}:${f.filiere}:${n.niveau}`;
                                                        const nOpen = isOpen(nKey);
                                                        return (
                                                            <div key={n.niveau} className="border-t border-rule">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => toggle(nKey)}
                                                                    className="w-full flex items-center gap-3 px-4 py-2 hover:bg-ink/5 transition-colors pl-12"
                                                                    data-testid={`tree-niveau-toggle-${y.year}-${f.filiere}-${n.niveau}`}
                                                                >
                                                                    {nOpen ? <ChevronDown size={12} className="text-ink" /> : <ChevronRight size={12} className="text-muted2" />}
                                                                    {nOpen ? <FolderOpen size={14} className="text-ink" /> : <Folder size={14} className="text-ink/60" />}
                                                                    <span className="text-sm">{n.label || NIVEAU_LABEL[n.niveau] || n.niveau}</span>
                                                                    <span className="pill bg-ink text-white text-[10px] !px-2 !py-0.5">{n.total}</span>
                                                                </button>
                                                                {nOpen && (
                                                                    <div className="pl-14">
                                                                        {(n.periodes || []).map((p) => {
                                                                            const pKey = `p:${y.year}:${f.filiere}:${n.niveau}:${p.period}`;
                                                                            const pOpen = isOpen(pKey);
                                                                            return (
                                                                                <div key={p.period} className="border-t border-rule">
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => toggle(pKey)}
                                                                                        className="w-full flex items-center gap-3 px-4 py-2 hover:bg-orange/5 transition-colors pl-14"
                                                                                        data-testid={`tree-period-toggle-${y.year}-${f.filiere}-${n.niveau}-${p.period}`}
                                                                                    >
                                                                                        {pOpen ? <ChevronDown size={11} className="text-muted2" /> : <ChevronRight size={11} className="text-muted2" />}
                                                                                        {pOpen ? <FolderOpen size={13} className="text-muted2" /> : <Folder size={13} className="text-muted2/70" />}
                                                                                        <span className="text-xs font-mono text-ink">{p.label}</span>
                                                                                        <span className="pill bg-emerald-50 text-emerald-700 border border-emerald-200 !px-1.5 !py-0 text-[10px]">{p.total}</span>
                                                                                    </button>
                                                                                    {pOpen && (
                                                                                        <ul className="pl-16 py-2 space-y-1" data-testid={`tree-bilans-list-${y.year}-${f.filiere}-${n.niveau}-${p.period}`}>
                                                                                            {p.bilans.map((b) => (
                                                                                                <TreeBilanRow key={b.id} b={b} isFormateur={isFormateur} />
                                                                                            ))}
                                                                                        </ul>
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function TreeBilanRow({ b, isFormateur }) {
    return (
        <li>
            <Link
                to={`/bilans/${b.id}`}
                className="flex flex-wrap items-center gap-3 px-3 py-2 -mx-3 hover:bg-orange/5 rounded-sm text-sm group"
                data-testid={`tree-bilan-${b.id}`}
            >
                <FileText size={14} className="text-orange shrink-0" />
                <span className="font-medium">{b.prenom} {b.nom}</span>
                {b.entreprise && (
                    <span className="text-[11px] font-mono text-muted2 inline-flex items-center gap-1">
                        <Building2 size={11} /> {b.entreprise}
                    </span>
                )}
                {b.tuteur && (
                    <span className="text-[11px] font-mono text-muted2 inline-flex items-center gap-1">
                        <GraduationCap size={11} /> {b.tuteur}
                    </span>
                )}
                {(b.date_debut || b.date_fin) && (
                    <span className="text-[11px] font-mono text-muted2 inline-flex items-center gap-1">
                        <Calendar size={11} /> {b.date_debut} → {b.date_fin}
                    </span>
                )}
                {b.tutor_evaluation && (
                    <span className="pill bg-emerald-50 text-emerald-700 border border-emerald-200 !px-1.5 !py-0 text-[10px] inline-flex items-center gap-1">
                        <Briefcase size={10} /> Tuteur ✓
                    </span>
                )}
                {isFormateur && b.author_email && (
                    <span className="text-[11px] font-mono text-muted2 truncate max-w-[180px]">{b.author_email}</span>
                )}
                <ChevronRight size={14} className="text-orange ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
        </li>
    );
}
